import { useState, type FormEvent } from 'react';
import { Button, cn, Input, Textarea } from '@/core/ui';
import { Check, PaperPlaneRight } from '@/core/ui/icons';
import { dateBounds, maskPhone, numberHint } from '../../domain/chat';
import type { Answer, Prompt } from '../../domain/types';

interface Props {
  prompt: Prompt;
  busy: boolean;
  /** A resposta recusada: por quê (aparece logo abaixo). */
  invalid: string | null;
  onAnswer: (answer: Answer) => void;
}

const PLACEHOLDERS: Partial<Record<Prompt['type'], string>> = {
  text: 'Digite sua resposta',
  email: 'nome@exemplo.com',
  phone: '(11) 98765-4321',
  number: 'Digite um número',
};

/** O campo de resposta da pergunta da vez — o formato depende do tipo. */
export function Composer({ prompt, busy, invalid, onAnswer }: Props) {
  return (
    <div className="space-y-2">
      {prompt.type === 'choice' ? (
        <Choices prompt={prompt} busy={busy} onAnswer={onAnswer} />
      ) : prompt.type === 'rating' ? (
        <Rating prompt={prompt} busy={busy} onAnswer={onAnswer} />
      ) : (
        <Typed prompt={prompt} busy={busy} onAnswer={onAnswer} />
      )}
      {invalid && (
        <p role="alert" className="text-xs text-rose-600">
          {invalid}
        </p>
      )}
    </div>
  );
}

type Part = Omit<Props, 'invalid'>;

function Choices({ prompt, busy, onAnswer }: Part) {
  const [picked, setPicked] = useState<string[]>([]);
  if (!prompt.multiple)
    return (
      <div className="flex flex-wrap justify-end gap-1.5">
        {prompt.choices.map((c) => (
          <button
            key={c.id}
            type="button"
            disabled={busy}
            onClick={() => onAnswer({ block_id: prompt.block_id, choices: [c.id] })}
            className="rounded-full bg-surface px-3.5 py-1.5 text-sm text-nav-active-fg ring-1 ring-brand-300 transition-colors hover:bg-nav-active disabled:opacity-50"
          >
            {c.label}
          </button>
        ))}
      </div>
    );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap justify-end gap-1.5">
        {prompt.choices.map((c) => {
          const on = picked.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={on}
              disabled={busy}
              onClick={() => setPicked((p) => (on ? p.filter((x) => x !== c.id) : [...p, c.id]))}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm ring-1 transition-colors disabled:opacity-50',
                on
                  ? 'bg-brand-400 text-on-brand ring-brand-400'
                  : 'bg-surface text-nav-active-fg ring-brand-300 hover:bg-nav-active',
              )}
            >
              {on && <Check aria-hidden className="size-3.5" />}
              {c.label}
            </button>
          );
        })}
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          loading={busy}
          disabled={picked.length === 0}
          onClick={() => onAnswer({ block_id: prompt.block_id, choices: picked })}
        >
          {prompt.button_label || 'Enviar'}
        </Button>
      </div>
    </div>
  );
}

function Rating({ prompt, busy, onAnswer }: Part) {
  const max = Math.max(1, prompt.rating_max);
  return (
    <div className="space-y-1.5">
      <div role="group" aria-label="Nota" className="flex flex-wrap justify-center gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            disabled={busy}
            onClick={() => onAnswer({ block_id: prompt.block_id, text: String(n) })}
            className="size-9 rounded-xl bg-surface text-sm font-medium text-fg ring-1 ring-line-strong transition-colors hover:bg-nav-active hover:text-nav-active-fg disabled:opacity-50"
          >
            {n}
          </button>
        ))}
      </div>
      {(prompt.low_label || prompt.high_label) && (
        <div className="flex justify-between text-[11px] text-fg-subtle">
          <span>{prompt.low_label}</span>
          <span>{prompt.high_label}</span>
        </div>
      )}
    </div>
  );
}

function Typed({ prompt, busy, onAnswer }: Part) {
  const [text, setText] = useState('');
  const bounds = prompt.type === 'date' ? dateBounds(prompt) : null;
  const hint = prompt.type === 'number' ? numberHint(prompt) : '';
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || busy) return;
    onAnswer({ block_id: prompt.block_id, text });
  };
  const placeholder = prompt.placeholder || PLACEHOLDERS[prompt.type] || '';

  return (
    <form onSubmit={submit} className="space-y-1">
      <div className="flex items-end gap-2">
        {prompt.type === 'text' && prompt.long ? (
          <Textarea
            aria-label="Sua resposta"
            rows={3}
            value={text}
            placeholder={placeholder}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
            }}
            className="min-h-0"
          />
        ) : (
          <Input
            aria-label="Sua resposta"
            autoFocus
            value={text}
            placeholder={placeholder}
            type={
              prompt.type === 'email'
                ? 'email'
                : prompt.type === 'date'
                  ? 'date'
                  : prompt.type === 'phone'
                    ? 'tel'
                    : 'text'
            }
            inputMode={prompt.type === 'number' ? 'decimal' : prompt.type === 'phone' ? 'tel' : undefined}
            min={bounds?.min}
            max={bounds?.max}
            onChange={(e) => setText(prompt.type === 'phone' ? maskPhone(e.target.value) : e.target.value)}
          />
        )}
        <Button
          type="submit"
          aria-label="Enviar resposta"
          loading={busy}
          disabled={!text.trim()}
          className="px-3"
        >
          {!busy && <PaperPlaneRight aria-hidden className="size-4" />}
        </Button>
      </div>
      {hint && <p className="text-[11px] text-fg-subtle">{hint}</p>}
    </form>
  );
}
